// home/FeatureShowcase.jsx
import React, { useState, useEffect } from 'react';
import {
  Shield, Users, Star, Clock, Bell, BarChart3,
  Zap, AlertTriangle, Crosshair, Eye, Lock,
  Smartphone, Server,
  X, ChevronLeft, ChevronRight
} from 'lucide-react';
import { getAssetUrl } from '../../utils/assetUrl';

const advancedFeatureCategories = [
  {
    id: "advanced-security",
    title: "Advanced Security",
    description: "Proactive threat detection and intelligent monitoring",
    features: [
      {
        icon: <Zap className="h-8 w-8 text-primary" />,
        title: "AI-Powered Analytics",
        description: "Advanced machine learning that transforms raw video data into actionable intelligence.",
        details: ["Behavior pattern recognition", "Anomaly detection", "Predictive security insights", "Automated threat assessment"]
      },
      {
        icon: <AlertTriangle className="h-8 w-8 text-accent-orange" />,
        title: "Suspicious Behavior Detection",
        description: "Identify potential security risks through intelligent behavior analysis.",
        details: ["Loitering detection", "Unusual movement pattern alerts", "Access attempt monitoring", "Activity pattern recognition"]
      },
      {
        icon: <Crosshair className="h-8 w-8 text-accent-orange" />,
        title: "Weapons Detection",
        description: "Advanced object recognition for enhanced threat detection.",
        details: ["Automated weapon recognition", "Immediate alert generation", "Visual verification", "Threat level classification"]
      },
      {
        icon: <Eye className="h-8 w-8 text-primary" />,
        title: "Cross-Camera Tracking",
        description: "Maintain visibility of subjects as they move throughout your facility.",
        details: ["Seamless subject tracking", "Movement path visualization", "Real-time location updates", "Zone transition alerts"]
      }
    ]
  },
  {
    id: "operations-management",
    title: "Operations & Management",
    description: "Tools that streamline daily security and administration",
    features: [
      {
        icon: <Lock className="h-8 w-8 text-primary" />,
        title: "Zone-Based Facility Security",
        description: "Control access to different areas of your facility with granular permissions.",
        details: ["Touchless access for authorized personnel", "Zone-based security permissions", "Real-time unauthorized access alerts", "Complete access audit trails"]
      },
      {
        icon: <Users className="h-8 w-8 text-primary" />,
        title: "Visitor & Front Desk Management",
        description: "Streamline reception operations and visitor check-in processes.",
        details: ["Automated visitor recognition", "Digital visitor logs", "Host notifications on arrival", "Streamlined visitor check-in"]
      },
      {
        icon: <Shield className="h-8 w-8 text-primary" />,
        title: "Command Center Integration",
        description: "Centralize security monitoring operations for maximum effectiveness.",
        details: ["Unified alert dashboard", "Multi-feed monitoring", "Alert escalation workflow", "Shift handover tools"]
      }
    ]
  },
  {
    id: "platform-compliance",
    title: "Platform & Compliance",
    description: "Enterprise-ready platform features and regulatory compliance",
    features: [
      {
        icon: <Users className="h-8 w-8 text-primary" />,
        title: "User Management",
        description: "Role-based access control and user administration.",
        details: ["Custom role creation", "Multi-factor authentication", "Single sign-on (SSO)", "User activity auditing"]
      },
      {
        icon: <Lock className="h-8 w-8 text-primary" />,
        title: "Privacy & Data Security",
        description: "Built-in tools for regulatory compliance and data protection.",
        details: ["End-to-end encryption", "GDPR/CCPA compliance", "Configurable data retention", "Detailed audit logging"]
      },
      {
        icon: <Smartphone className="h-8 w-8 text-primary" />,
        title: "Mobile Access",
        description: "Secure platform access from anywhere.",
        details: ["Mobile alert notifications", "Remote system management", "Field team coordination", "Cross-platform compatibility"]
      },
      {
        icon: <Server className="h-8 w-8 text-primary" />,
        title: "API Ecosystem",
        description: "Integration capabilities with third-party systems.",
        details: ["Comprehensive REST API", "Access control system integration", "HR/payroll connections", "Custom integration options"]
      }
    ]
  }
];

export function FeatureShowcase() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState('facial-recognition');

  const features = [
    {
      id: "facial-recognition",
      icon: <Shield className="h-12 w-12 text-primary" />,
      title: "Facial Recognition",
      description: "State-of-the-art algorithms that accurately identify individuals in real-time, even in challenging lighting conditions.",
      imageUrl: getAssetUrl('/kg-facial-recognition.png'),
      benefits: [
        "Multi-face detection in a single frame",
        "Adapts to appearance changes over time",
        "Configurable confidence thresholds",
        "Works across varying lighting conditions"
      ]
    },
    {
      id: "access-control",
      icon: <Users className="h-12 w-12 text-primary" />,
      title: "Access Control",
      description: "Automated whitelist/blacklist management that instantly alerts security personnel to unauthorized individuals.",
      imageUrl: getAssetUrl('/kg-access-control.png'),
      benefits: [
        "Real-time alerts for unauthorized access",
        "Easy whitelist/blacklist management",
        "Bulk import of personnel data",
        "Complete event logging with timestamps"
      ]
    },
    {
      id: "vip-identification",
      icon: <Star className="h-12 w-12 text-primary" />,
      title: "VIP Identification",
      description: "Ensure important guests receive the attention they deserve with instant notifications when VIPs arrive.",
      imageUrl: getAssetUrl('/kg-vip-arrival.png'),
      benefits: [
        "Special alerts for VIP detection",
        "Customizable VIP categories",
        "Visit tracking and reporting",
        "Streamlined guest management"
      ]
    },
    {
      id: "time-attendance",
      icon: <Clock className="h-12 w-12 text-primary" />,
      title: "Time & Attendance",
      description: "Eliminate time cards and sign-in sheets with automated attendance tracking for students and employees.",
      imageUrl: getAssetUrl('/kg-time-attendance.png'),
      benefits: [
        "Automated clock-in/out for employees",
        "Student attendance monitoring",
        "Tardiness tracking and reporting",
        "Optional parent/manager notifications"
      ]
    },
    {
      id: "real-time-alerts",
      icon: <Bell className="h-12 w-12 text-primary" />,
      title: "Real-time Alerts",
      description: "Receive instant notifications about security concerns across multiple channels—email, SMS, or in-app.",
      imageUrl: getAssetUrl('/kg-realtime-alerts.png'),
      benefits: [
        "Customizable alert rules",
        "Multiple notification channels",
        "Priority-based alerting",
        "Alert history and management"
      ]
    },
    {
      id: "analytics",
      icon: <BarChart3 className="h-12 w-12 text-primary" />,
      title: "Comprehensive Analytics",
      description: "Transform security data into actionable insights with powerful reporting and dashboard tools.",
      imageUrl: getAssetUrl('/kg-analytics.png'),
      benefits: [
        "Customizable dashboards",
        "Exportable reports in multiple formats",
        "Trend analysis and visualization",
        "Scheduled reporting automation"
      ]
    }
  ];

  const activeFeatureData = features.find(feature => feature.id === activeFeature);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isModalOpen]);

  // Close modal on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  return (
    <div id="features" className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-foreground sm:text-4xl">
            Powerful Features for Modern Physical Security
          </h2>
          <p className="mt-4 text-xl text-muted-foreground max-w-2xl mx-auto">
            The full capability stack of the Autonomous SOC — from multi-modal detection to enterprise compliance.
          </p>
        </div>

        {/* Category Carousel */}
        <div className="mt-16 relative">
          <div className="overflow-hidden">
            {advancedFeatureCategories.map((category, index) => (
              <div
                key={category.id}
                className={index === activeSlide ? 'block' : 'hidden'}
              >
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-foreground">
                    {category.title}
                  </h3>
                  <p className="mt-2 text-lg text-muted-foreground">
                    {category.description}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {category.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="bg-card rounded-lg border border-border p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="p-2 bg-primary/10 rounded-lg w-fit">
                        {feature.icon}
                      </div>
                      <h4 className="mt-4 text-lg font-semibold text-card-foreground">
                        {feature.title}
                      </h4>
                      <p className="mt-2 text-sm text-card-foreground/80">
                        {feature.description}
                      </p>
                      <ul className="mt-4 space-y-1">
                        {feature.details.map((detail, detailIdx) => (
                          <li key={detailIdx} className="flex items-start text-sm">
                            <span className="flex-shrink-0 text-primary mr-2">•</span>
                            <span className="text-card-foreground/70">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Prev arrow */}
          <button
            onClick={() => setActiveSlide((prev) => (prev - 1 + advancedFeatureCategories.length) % advancedFeatureCategories.length)}
            className="absolute top-1/2 -translate-y-1/2 -left-4 md:-left-6 p-2 rounded-full bg-card border border-border shadow hover:bg-accent transition-colors"
            aria-label="Previous category"
          >
            <ChevronLeft className="h-5 w-5 text-foreground" />
          </button>

          {/* Next arrow */}
          <button
            onClick={() => setActiveSlide((prev) => (prev + 1) % advancedFeatureCategories.length)}
            className="absolute top-1/2 -translate-y-1/2 -right-4 md:-right-6 p-2 rounded-full bg-card border border-border shadow hover:bg-accent transition-colors"
            aria-label="Next category"
          >
            <ChevronRight className="h-5 w-5 text-foreground" />
          </button>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {advancedFeatureCategories.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveSlide(index)}
                className={`h-2.5 w-2.5 rounded-full transition-colors ${
                  index === activeSlide ? 'bg-primary' : 'bg-muted-foreground/30'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* More Features button */}
        <div className="mt-12 text-center">
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            More Features
          </button>
        </div>
      </div>

      {/* Feature Details Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setIsModalOpen(false)}
          />

          {/* Modal panel */}
          <div className="relative z-10 w-full max-w-5xl max-h-[90vh] overflow-y-auto mx-4 rounded-lg bg-background border border-border shadow-xl">
            {/* Close button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 p-1 rounded-md hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-foreground" />
            </button>

            <div className="p-6 md:p-8">
              <h3 className="text-2xl font-bold text-foreground mb-6">
                Core Capabilities
              </h3>

              {/* Tab bar */}
              <div className="border-b border-muted">
                <div className="flex overflow-x-auto">
                  {features.map((feature) => (
                    <button
                      key={feature.id}
                      onClick={() => setActiveFeature(feature.id)}
                      className={`px-1 py-4 text-sm font-medium whitespace-nowrap border-b-2 ${
                        activeFeature === feature.id
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                      } -mb-px mr-8`}
                    >
                      {feature.title}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab content */}
              <div className="mt-8">
                <div className="rounded-lg bg-card shadow overflow-hidden">
                  <div className="md:flex">
                    <div className="md:w-1/2 p-6 md:p-8">
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          {activeFeatureData.icon}
                        </div>
                        <h3 className="text-2xl font-semibold text-card-foreground">
                          {activeFeatureData.title}
                        </h3>
                      </div>

                      <p className="mt-4 text-card-foreground/80">
                        {activeFeatureData.description}
                      </p>

                      <div className="mt-6">
                        <h4 className="text-lg font-medium text-card-foreground mb-4">Key Benefits</h4>
                        <ul className="space-y-2">
                          {activeFeatureData.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                              <span className="ml-2 text-card-foreground/70">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="md:w-1/2 bg-muted/50 flex items-center justify-center p-6 md:p-8">
                      <div className="text-center">
                        <img
                          src={activeFeatureData.imageUrl}
                          alt={`${activeFeatureData.title} illustration`}
                          className="w-[320px] h-[480px] object-cover rounded-lg shadow-md mx-auto"
                        />
                        <p className="mt-3 text-sm text-muted-foreground">
                          {activeFeatureData.title} in action
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
